import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, AreaChart, Area,
} from 'recharts';
import { dashboardService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { DowLoader } from '../components/DowLoader';
import { RangePicker, rangeParams, BLANK_RANGE_FILTERS } from '../components/reports/ReportRange';
import { SearchableSelect } from '../components/SearchableSelect';
import './Dashboard.css';

// ── Palette (mirrors the CSS variables) ──────────────────────────────────────
const C = {
  accent: '#ef6c1a',   // the app's orange (sidebar active colour)
  red: '#e8001d', blue: '#3b82f6', green: '#10b981', amber: '#f59e0b',
  purple: '#8b5cf6', teal: '#14b8a6', pink: '#ec4899', orange: '#f97316',
  indigo: '#6366f1', cyan: '#06b6d4', slate: '#334155',
};
const PALETTE = [C.blue, C.green, C.amber, C.purple, C.teal, C.red, C.pink, C.orange, C.indigo, C.cyan];
const STATUS_META = {
  new:         { label: 'New',         color: C.blue },
  in_progress: { label: 'In Progress', color: C.purple },
  follow_up:   { label: 'Follow Up',   color: C.amber },
  converted:   { label: 'Booked',      color: C.green },
  lost:        { label: 'Lost',        color: C.red },
};
const SOURCE_LABEL = (s) => (s || 'other').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const MAX_ATTEMPTS = 10;

const pct    = (part, total) => (total > 0 ? (part / total) * 100 : 0);
const pctStr = (part, total, dp = 1) => `${pct(part, total).toFixed(dp)}%`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

// ── Shared chart bits ────────────────────────────────────────────────────────
const AXIS = { tick: { fill: '#6b7280', fontSize: 11 }, axisLine: { stroke: '#e5e7eb' }, tickLine: false };
const GRID = { stroke: 'rgba(15,23,42,0.06)', vertical: false };
const CURSOR = { fill: 'rgba(15,23,42,0.04)' };
const legendText = (value) => <span style={{ color: '#6b7280', fontSize: 12 }}>{value}</span>;

// Dark tooltip shared by every chart. `fmt(item, payloadRow)` turns one
// series entry into its display text — usually "value (x% of …)".
function DarkTip({ active, payload, label, fmt, hideLabel }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="dow-tip">
      {!hideLabel && <div className="lbl">{label ?? payload[0]?.name}</div>}
      {payload.map((p, i) => (
        <div className="row" key={i}>
          <span className="dot" style={{ background: p.payload?.fill || p.payload?.color || p.color || p.fill }} />
          <span>{fmt ? fmt(p, p.payload) : `${p.name}: ${p.value}`}</span>
        </div>
      ))}
    </div>
  );
}

const Empty = ({ text = 'No data in this view.' }) => (
  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 12 }}>{text}</div>
);

// ── Small building blocks ────────────────────────────────────────────────────
function Kpi({ color, icon, value, label, sub, ringPct }) {
  const p = Math.max(0, Math.min(100, ringPct || 0));
  return (
    <div className="dow-kpi" style={{ '--kpi': color }}>
      <div className="dow-kpi-body">
        <div className="dow-kpi-icon">{icon}</div>
        <div className="dow-kpi-val">{value}</div>
        <div className="dow-kpi-label">{label}</div>
        <div className="dow-kpi-sub">{sub}</div>
      </div>
      <div className="dow-ring" style={{ background: `conic-gradient(${color} ${p}%, var(--surface) ${p}% 100%)` }}>
        <span>{Math.round(p)}%</span>
      </div>
    </div>
  );
}

function Card({ title, badge, children, className = '' }) {
  return (
    <div className={`dow-card ${className}`}>
      <div className="dow-sec-head">
        <h2>{title}</h2>
        {badge && <span className="dow-badge">{badge}</span>}
      </div>
      {children}
    </div>
  );
}

function Insight({ icon, color, title, chip, desc }) {
  return (
    <div className="dow-insight">
      <div className={`dow-insight-icon ${color}`}>{icon}</div>
      <div>
        <div className="dow-insight-title">{title}{chip && <span className="dow-chip">{chip}</span>}</div>
        <div className="dow-insight-desc">{desc}</div>
      </div>
    </div>
  );
}

function RankTable({ headers, rows, empty }) {
  return (
    <div className="dow-table">
      <table>
        <thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length ? rows : <tr><td colSpan={headers.length} style={{ color: 'var(--muted)' }}>{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ── Charts ───────────────────────────────────────────────────────────────────
function MonthLeadsChart({ months }) {
  const sum  = months.reduce((a, m) => a + m.total, 0);
  const data = months.map((m, i) => ({ ...m, fill: PALETTE[i % PALETTE.length] }));
  if (!months.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid {...GRID} />
        <XAxis dataKey="label" {...AXIS} />
        <YAxis {...AXIS} allowDecimals={false} />
        <Tooltip cursor={CURSOR} content={<DarkTip fmt={(p) => `Total Leads: ${p.value} (${pctStr(p.value, sum)})`} />} />
        <Bar dataKey="total" radius={[8, 8, 0, 0]}>
          {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function QualMonthChart({ months }) {
  if (!months.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={months} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid {...GRID} />
        <XAxis dataKey="label" {...AXIS} />
        <YAxis {...AXIS} allowDecimals={false} />
        <Tooltip cursor={CURSOR} content={<DarkTip fmt={(p, row) => `${p.name}: ${p.value} (${pctStr(p.value, row.total)})`} />} />
        <Legend formatter={legendText} />
        <Bar dataKey="qualified"    name="Qualified"     stackId="a" fill={C.blue} radius={[0, 0, 0, 0]} />
        <Bar dataKey="nonQualified" name="Non-Qualified" stackId="a" fill={C.red}  radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function HBarChart({ data, color, total, suffix = '' }) {
  const rows = data.map((d, i) => ({ ...d, fill: color || PALETTE[i % PALETTE.length] }));
  if (!rows.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
        <CartesianGrid stroke="rgba(15,23,42,0.06)" horizontal={false} />
        <XAxis type="number" {...AXIS} allowDecimals={false} />
        <YAxis type="category" dataKey="name" width={130} {...AXIS} tick={{ fill: '#4b5563', fontSize: 11 }} />
        <Tooltip cursor={CURSOR} content={<DarkTip fmt={(p) => `${p.value} (${pctStr(p.value, total)}${suffix})`} />} />
        <Bar dataKey="count" radius={[0, 6, 6, 0]}>
          {rows.map((d, i) => <Cell key={i} fill={d.fill} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function Doughnut({ data, colors, inner = '60%' }) {
  const total = data.reduce((a, d) => a + d.count, 0);
  if (!total) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data} dataKey="count" nameKey="name" innerRadius={inner} outerRadius="85%" paddingAngle={data.length > 1 ? 2 : 0} stroke="#ffffff" strokeWidth={3}>
          {data.map((d, i) => <Cell key={i} fill={d.color || colors?.[i] || PALETTE[i % PALETTE.length]} />)}
        </Pie>
        <Tooltip content={<DarkTip hideLabel fmt={(p) => `${p.name}: ${p.value} (${pctStr(p.value, total)})`} />} />
        <Legend formatter={legendText} iconType="circle" />
      </PieChart>
    </ResponsiveContainer>
  );
}

function VBarChart({ data, total, suffix = '' }) {
  const rows = data.map((d, i) => ({ ...d, fill: PALETTE[i % PALETTE.length] }));
  if (!rows.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid {...GRID} />
        <XAxis dataKey="name" {...AXIS} interval={0} tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={v => (v.length > 14 ? `${v.slice(0, 13)}…` : v)} />
        <YAxis {...AXIS} allowDecimals={false} />
        <Tooltip cursor={CURSOR} content={<DarkTip fmt={(p) => `${p.value} (${pctStr(p.value, total)}${suffix})`} />} />
        <Bar dataKey="count" radius={[6, 6, 0, 0]}>
          {rows.map((d, i) => <Cell key={i} fill={d.fill} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function DateVolumeChart({ data }) {
  if (!data.length) return <Empty text="No activity logged in this view." />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <defs>
          <linearGradient id="dowTeal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.teal} stopOpacity={0.35} />
            <stop offset="100%" stopColor={C.teal} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid {...GRID} />
        <XAxis dataKey="label" {...AXIS} minTickGap={24} />
        <YAxis {...AXIS} allowDecimals={false} />
        <Tooltip content={<DarkTip fmt={(p) => `${p.value} ${p.value === 1 ? 'activity' : 'activities'} logged`} />} />
        <Area type="monotone" dataKey="count" stroke={C.teal} strokeWidth={2} fill="url(#dowTeal)" dot={{ r: 2, fill: C.teal, strokeWidth: 0 }} activeDot={{ r: 5 }} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function BookingChart({ months }) {
  if (!months.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={months} margin={{ top: 8, right: -8, left: -16, bottom: 0 }}>
        <CartesianGrid {...GRID} />
        <XAxis dataKey="label" {...AXIS} />
        <YAxis yAxisId="left" {...AXIS} allowDecimals={false} />
        <YAxis yAxisId="right" orientation="right" {...AXIS} allowDecimals={false} />
        <Tooltip cursor={CURSOR} content={<DarkTip fmt={(p, row) => (p.dataKey === 'booked' ? `Bookings: ${p.value} (${pctStr(p.value, row.total)} booking rate)` : `Total Leads: ${p.value}`)} />} />
        <Legend formatter={legendText} />
        <Bar yAxisId="left"  dataKey="total"  name="Total Leads" fill="rgba(59,130,246,0.3)" stroke={C.blue} strokeWidth={2} radius={[6, 6, 0, 0]} />
        <Bar yAxisId="right" dataKey="booked" name="Bookings"    fill={C.green} radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function BucketOutcomeChart({ buckets }) {
  if (!buckets.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={buckets} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
        <CartesianGrid {...GRID} />
        <XAxis dataKey="bucket" {...AXIS} interval={0} tick={{ fill: '#6b7280', fontSize: 10 }} />
        <YAxis {...AXIS} tickFormatter={v => `${v}%`} />
        <Tooltip cursor={CURSOR} content={<DarkTip fmt={(p, row) => `${p.name}: ${p.dataKey === 'bookedRate' ? row.booked : row.lost} of ${row.total} leads (${Number(p.value).toFixed(1)}%)`} />} />
        <Legend formatter={legendText} />
        <Bar dataKey="bookedRate" name="Booked" fill={C.green} radius={[6, 6, 0, 0]} />
        <Bar dataKey="lostRate"   name="Lost"   fill={C.red}   radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function QualSplitChart({ qual, nqual }) {
  const data = [
    { name: 'Booked', qualified: qual.booked, notQualified: nqual.booked },
    { name: 'Live',   qualified: qual.live,   notQualified: nqual.live },
    { name: 'Lost',   qualified: qual.lost,   notQualified: nqual.lost },
  ];
  const qSum = qual.booked + qual.live + qual.lost;
  const nSum = nqual.booked + nqual.live + nqual.lost;
  if (!qSum && !nSum) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid {...GRID} />
        <XAxis dataKey="name" {...AXIS} />
        <YAxis {...AXIS} allowDecimals={false} />
        <Tooltip cursor={CURSOR} content={<DarkTip fmt={(p) => `${p.name}: ${p.value} (${pctStr(p.value, p.dataKey === 'qualified' ? qSum : nSum)} of that cohort)`} />} />
        <Legend formatter={legendText} />
        <Bar dataKey="qualified"    name="Qualified"     fill={C.blue}  radius={[6, 6, 0, 0]} />
        <Bar dataKey="notQualified" name="Not Qualified" fill={C.amber} radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export const Dashboard = () => {
  const { user } = useAuth();
  const [month, setMonth]     = useState('');
  const [dealerId, setDealer] = useState('');
  // Load a recent window by default so the board stays fast as the workspace
  // grows; a longer preset, or an explicit from/to, is one click away.
  // Opens on the last 7 days (reports keep their 30-day default)
  const [range, setRange]     = useState({ ...BLANK_RANGE_FILTERS, range: '7d' });
  // Which date puts a lead in a month: when it came in, or when it was last
  // updated — the second shows a month's work on leads from earlier months.
  // Nothing chosen = the board's usual view (by when the lead came in).
  const [dateBy, setDateBy]   = useState('');

  // One request per dealer scope, window and date basis; the payload carries
  // every month's numbers, so switching months is a client-side lookup.
  const { data, isLoading, isFetching, isError } = useQuery(
    ['dashAnalytics', dealerId, range, dateBy],
    () => dashboardService.getAnalytics({ dealerId: dealerId || undefined, dateBy: dateBy || undefined, ...rangeParams(range) }),
    { keepPreviousData: true, staleTime: 60_000 }
  );
  const payload = data?.data?.data;
  const meta    = payload?.meta;
  const months  = useMemo(() => payload?.months || [], [payload]);
  const dealers = payload?.dealers || [];
  const s       = (month && payload?.byMonth?.[month]) || payload?.all;

  // Derived numbers the insight cards and notes share.
  const derived = useMemo(() => {
    if (!s) return null;
    const lostSum      = s.lostReasons.reduce((a, r) => a + r.count, 0);
    const modelSum     = s.models.reduce((a, m) => a + m.count, 0);
    const worked       = s.bucketOutcome.filter(b => b.bucket !== '0 Attempts' && b.total > 0);
    const peak         = worked.slice().sort((a, b) => b.bookedRate - a.bookedRate)[0] || null;
    const low          = s.bucketOutcome.filter(b => ['0 Attempts', '1 Attempt', '2-3 Attempts'].includes(b.bucket));
    const lowTotal     = low.reduce((a, b) => a + b.total, 0);
    const lowBooked    = low.reduce((a, b) => a + b.booked, 0);
    const lowLost      = low.reduce((a, b) => a + b.lost, 0);
    const highTotal    = s.total - lowTotal;
    const highBooked   = s.booked - lowBooked;
    const highLost     = s.lost - lowLost;
    const topBucket10  = s.bucketOutcome.find(b => b.bucket === '10+ Attempts');
    const hot          = s.category.find(c => c.name === 'Hot')?.count || 0;
    const slow         = (s.category.find(c => c.name === 'Cold')?.count || 0);
    const topSource    = s.sources[0];
    const topExec      = s.executives[0];
    const unspecified  = s.lostReasons.find(r => r.name === 'Not specified')?.count || 0;
    return { lostSum, modelSum, peak, lowTotal, lowBooked, lowLost, highTotal, highBooked, highLost, topBucket10, hot, slow, topSource, topExec, unspecified };
  }, [s]);

  const statusData = useMemo(() => (s
    ? Object.entries(STATUS_META).map(([key, m]) => ({ name: m.label, count: s.statusDist[key] || 0, color: m.color })).filter(d => d.count > 0)
    : []), [s]);
  const categoryData = useMemo(() => (s
    ? s.category.map(c => ({ ...c, color: { Hot: C.red, Warm: C.amber, Cold: C.blue, 'Not Tagged': C.slate }[c.name] || C.purple }))
    : []), [s]);
  const attemptsData = useMemo(() => (s
    ? s.attempts.map((a, i) => ({ name: a.bucket, count: a.count, color: [C.slate, C.red, C.amber, C.blue, C.purple, C.green][i] })).filter(d => d.count > 0)
    : []), [s]);

  const topModels  = s ? s.models.slice(0, 8) : [];
  const topLost    = s ? s.lostReasons.slice(0, 10) : [];
  const topExecs   = s ? s.executives.slice(0, 10).map(e => ({ ...e, name: e.name })) : [];
  const execSum    = topExecs.reduce((a, e) => a + e.count, 0);

  const monthLabelOf = (key) => months.find(m => m.key === key)?.label || 'All Months';
  const scopeLabel   = !month ? 'across all leads'
    : dateBy === 'updated' ? `for leads updated in ${monthLabelOf(month)}`
    : `for ${monthLabelOf(month)} leads`;

  return (
    <div className="dow">
      {/* Top bar */}
      <div className="dow-topbar">
        <div className="dow-logo">
          <div>
            <h1>Dashboard</h1>
            <p>Welcome back, <strong>{user?.firstName}</strong>! Here's your overview.</p>
          </div>
        </div>
        <div className="dow-meta">
          <div className="dow-pill">Total Leads <span>{meta ? fmtNum(meta.totalLeads) : '—'}</span></div>
          <div className="dow-pill">Months Covered <span>{meta?.monthsCovered ?? '—'}</span></div>
          <div className="dow-pill">Last Updated <span>{meta ? new Date(meta.lastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span></div>
          <button className="dow-btn" onClick={() => window.print()}>⬇ Download PDF</button>
        </div>
      </div>

      {/* Dealer filter — one picker rather than a row of buttons, because a
          workspace's dealer list has no ceiling and a dozen already wrapped
          onto a second line. */}
      {dealers.length > 0 && (
        <div className="dow-dealerbar">
          <span className="dow-filter-label">Dealer:</span>
          <SearchableSelect
            value={dealerId}
            onChange={val => { setDealer(val); setMonth(''); }}
            options={dealers.map(d => ({ value: String(d._id), label: d.name }))}
            allLabel="All Dealers"
            placeholder="Search dealer…"
            loading={isFetching}
            spinner={<span className="dow-spin" />}
            buttonClassName={`dow-filter-btn ${dealerId ? 'active' : ''}`}
          />
          <span className="dow-filter-note">
            {dealers.length} dealer{dealers.length === 1 ? '' : 's'}
          </span>
        </div>
      )}

      {/* Month filter + how far back to load */}
      <div className="dow-filterbar">
        <span className="dow-filter-label">Month by:</span>
        <select
          value={dateBy}
          onChange={e => { setDateBy(e.target.value); setMonth(''); }}
          className="dow-range"
          title="Insert Date: the month a lead came in. Update Date: the month a lead was last updated."
        >
          <option value="">Select date type</option>
          <option value="insert">By Insert Date</option>
          <option value="updated">By Update Date</option>
        </select>
        <span className="dow-filter-label">Filter by Month:</span>
        <button className={`dow-filter-btn ${!month ? 'active' : ''}`} onClick={() => setMonth('')}>All Months</button>
        {months.map(m => (
          <button key={m.key} className={`dow-filter-btn ${month === m.key ? 'active' : ''}`} onClick={() => setMonth(m.key)}>{m.label}</button>
        ))}
        <span className="dow-filter-label" style={{ marginLeft: 'auto' }}>Show:</span>
        <RangePicker
          value={range}
          onChange={next => { setRange(next); setMonth(''); }}
          inputClassName="dow-range"
          labelClassName="dow-filter-label"
        />
      </div>
      {/* While a filter change loads, the stale board is dimmed so it's
          obvious the numbers aren't final yet. */}
      <div className={`dow-main ${isFetching && s ? 'fetching' : ''}`}>
        {isLoading && !s && <div className="dow-loading"><DowLoader size="xl" label="Loading analytics" /></div>}
        {isError && <div className="dow-empty"><h2>Couldn't load analytics</h2><p>Please refresh and try again.</p></div>}

        {s && !s.total && (
          <div className="dow-empty dow-mb"><h2>No leads in this view</h2><p>Try another month or dealer.</p></div>
        )}

        {s && derived && (
          <>
            {/* KPIs */}
            <div className="dow-kpi-row">
              <Kpi color={C.accent} icon="📋" value={fmtNum(s.total)}     label="Total Leads"     sub="100% base for this view" ringPct={100} />
              <Kpi color={C.blue}   icon="✅" value={fmtNum(s.qualified)} label="Qualified Leads" sub={`${pctStr(s.qualified, s.total)} of total`} ringPct={pct(s.qualified, s.total)} />
              <Kpi color={C.green}  icon="🚗" value={fmtNum(s.booked)}    label="Cars Booked"     sub={`${s.retail ? `${s.bookings} booked · ${s.retail} retail · ` : ''}${pctStr(s.booked, s.total)} of total · ${pctStr(s.booked, s.qualified)} of qualified`} ringPct={pct(s.booked, s.total)} />
              <Kpi color={C.amber}  icon="🔄" value={fmtNum(s.live)}      label="Live Leads"      sub={`${pctStr(s.live, s.total)} of total · active pipeline`} ringPct={pct(s.live, s.total)} />
              <Kpi color={C.purple} icon="📞" value={s.avgAttempts}       label="Avg Attempts"    sub={`${pctStr(s.avgAttempts, MAX_ATTEMPTS)} of max (${MAX_ATTEMPTS})`} ringPct={pct(s.avgAttempts, MAX_ATTEMPTS)} />
              <Kpi color={C.teal}   icon="❌" value={fmtNum(s.lost)}      label="Lost Leads"      sub={`${pctStr(s.lost, s.total)} lost rate`} ringPct={pct(s.lost, s.total)} />
            </div>

            {/* Upcoming prospects */}
            <Card className="dow-mb" title="📅 Upcoming Prospects — Scheduled Follow-ups" badge={`${s.watchlist.count} live leads with a follow-up scheduled`}>
              <div className="dow-note lead">
                Live leads (not yet Lost or Booked) whose next follow-up date is today or later — a real count taken from the
                follow-up dates logged on each lead, not a projection.
              </div>
              <div className="dow-effort three flush">
                <div className="dow-effort-box high">
                  <div className="val">{s.watchlist.count}</div>
                  <div className="lbl">Live leads with a follow-up scheduled</div>
                </div>
                <div className="dow-effort-box low">
                  <div className="val small">{s.watchlist.byMonth.map(m => `${m.label.split(' ')[0]}: ${m.count}`).join(' · ') || '—'}</div>
                  <div className="lbl">Split by follow-up month</div>
                </div>
                <div className="dow-effort-box high">
                  <div className="val">{s.watchlist.byCategory[0]?.name || '—'}</div>
                  <div className="lbl">{s.watchlist.byCategory[0]?.count || 0} of {s.watchlist.count} are this category</div>
                </div>
              </div>
            </Card>

            {/* Activity volume */}
            <Card className="dow-mb" title="🗓️ Activity Log — Date-wise Update Volume" badge={`${fmtNum(s.dateVolume.reduce((a, d) => a + d.count, 0))} activities across ${s.dateVolume.length} dates`}>
              <div className="dow-chart tall"><DateVolumeChart data={s.dateVolume} /></div>
              <div className="dow-note">
                Counts every activity (call, visit, close) logged {scopeLabel} ({fmtNum(s.total)} leads in this view) by the day it was
                recorded — filtered by the month filter above, same as the KPI cards.
              </div>
            </Card>

            {/* Funnel + qualified → lost */}
            <div className="dow-grid-2">
              <Card title="Prospect → Outcome Funnel" badge="% of total">
                <div className="dow-funnel">
                  {[
                    { label: 'Total Leads',   val: s.total,     color: C.blue },
                    { label: 'Qualified',     val: s.qualified, color: C.purple },
                    { label: 'Live / Active', val: s.live,      color: C.amber },
                    { label: 'Booked',        val: s.booked,    color: C.green },
                    { label: 'Lost',          val: s.lost,      color: C.red },
                  ].map(st => (
                    <div className="dow-funnel-row" key={st.label}>
                      <span className="dow-funnel-label">{st.label}</span>
                      <div className="dow-funnel-track">
                        <div className="dow-funnel-fill" style={{ width: `${Math.max(pct(st.val, s.total), 4)}%`, background: st.color }}>{fmtNum(st.val)}</div>
                      </div>
                      <span className="dow-funnel-sub">{pctStr(st.val, s.total)} of total</span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card title="Qualified Prospects Who Went Lost" badge="Real crosstab from lost activities">
                <div className="dow-effort" style={{ marginBottom: 14 }}>
                  <div className="dow-effort-box high"><div className="val">{pctStr(s.qualOutcome.booked, s.qualified, 0)}</div><div className="lbl">Qualified → Booked ({s.qualOutcome.booked} of {s.qualified})</div></div>
                  <div className="dow-effort-box low"><div className="val">{pctStr(s.qualOutcome.lost, s.qualified, 0)}</div><div className="lbl">Qualified → Lost ({s.qualOutcome.lost} of {s.qualified})</div></div>
                </div>
                <RankTable
                  headers={['#', 'Lost Reason (Qualified cohort)', 'Count', 'Share %']}
                  empty="No qualified-then-lost leads in this view."
                  rows={(() => {
                    const sum = s.qualLostReasons.reduce((a, r) => a + r.count, 0) || 1;
                    return s.qualLostReasons.map((r, i) => (
                      <tr key={r.name}>
                        <td className="dow-rank">{i + 1}</td>
                        <td>{r.name}</td>
                        <td><strong>{r.count}</strong></td>
                        <td><div className="dow-bar-cell"><div className="dow-mini-bar" style={{ width: `${Math.round(pct(r.count, sum)) * 3}px`, background: PALETTE[i % PALETTE.length] }} /><span style={{ fontSize: 11, color: 'var(--muted)' }}>{pctStr(r.count, sum)}</span></div></td>
                      </tr>
                    ));
                  })()}
                />
              </Card>
            </div>

            {/* Month-wise */}
            <div className="dow-grid-2">
              <Card title="Month-wise Lead Count" badge={`${months.length} month(s)`}>
                <div className="dow-chart"><MonthLeadsChart months={months} /></div>
              </Card>
              <Card title="Month-wise Qualification Breakup" badge="Qualified vs Non-Qualified">
                <div className="dow-chart"><QualMonthChart months={months} /></div>
              </Card>
            </div>

            {/* Model + status */}
            <div className="dow-grid-65">
              <Card title="Model-wise Lead Count" badge="Top models">
                <div className="dow-chart tall"><HBarChart data={topModels} total={derived.modelSum} /></div>
              </Card>
              <Card title="Lead Status Distribution" badge="Current pipeline">
                <div className="dow-chart tall"><Doughnut data={statusData} inner="65%" /></div>
              </Card>
            </div>

            {/* Lost + attempts */}
            <div className="dow-grid-2">
              <Card title="Lost Reason Analysis" badge="All lost leads">
                <div className="dow-chart tall"><HBarChart data={topLost} total={derived.lostSum} suffix=" of lost" /></div>
              </Card>
              <Card title="No. of Attempts Distribution" badge="Follow-up intensity">
                <div className="dow-chart tall"><Doughnut data={attemptsData} /></div>
              </Card>
            </div>

            {/* Category + executives */}
            <div className="dow-grid-56">
              <Card title="Lead Category Breakdown" badge="By buying time">
                <div className="dow-chart"><Doughnut data={categoryData} inner="0%" /></div>
                <div className="dow-note">
                  {s.noCategory} leads ({pctStr(s.noCategory, s.total)}) have no buying time tagged — Hot = immediate, Warm = within a month,
                  Cold = 3–6 months or just enquiring. Closing that gap would sharpen prioritisation.
                </div>
              </Card>
              <Card title="Executive-wise Lead Volume" badge="Current owner">
                <div className="dow-chart"><VBarChart data={topExecs} total={execSum} suffix=" of shown" /></div>
                <div className="dow-note">
                  {s.heldAtAdmin} leads ({pctStr(s.heldAtAdmin, s.total)}) are still held at admin level — not yet with a dealer or sales executive.
                </div>
              </Card>
            </div>

            {/* Detail tables */}
            <div className="dow-grid-2">
              <div>
                <div className="dow-sec-head"><h2>Top Lost Reasons — Detail</h2></div>
                <RankTable
                  headers={['#', 'Lost Reason', 'Count', 'Share %']}
                  empty="No lost leads in this view."
                  rows={topLost.map((r, i) => (
                    <tr key={r.name}>
                      <td className="dow-rank">{i + 1}</td>
                      <td>{r.name}</td>
                      <td><strong>{r.count}</strong></td>
                      <td><div className="dow-bar-cell"><div className="dow-mini-bar" style={{ width: `${Math.round(pct(r.count, derived.lostSum)) * 4}px`, background: PALETTE[i % PALETTE.length] }} /><span style={{ fontSize: 11, color: 'var(--muted)' }}>{pctStr(r.count, derived.lostSum)}</span></div></td>
                    </tr>
                  ))}
                />
              </div>
              <div>
                <div className="dow-sec-head"><h2>Model-wise Leads — Detail</h2></div>
                <RankTable
                  headers={['#', 'Model', 'Leads', 'Share %']}
                  empty="No model captured on leads in this view."
                  rows={topModels.map((m, i) => (
                    <tr key={m.name}>
                      <td className="dow-rank">{i + 1}</td>
                      <td><span className={`dow-tag ${['dow-tag-blue', 'dow-tag-green', 'dow-tag-amber', 'dow-tag-purple', 'dow-tag-red'][i % 5]}`}>{m.name}</span></td>
                      <td><strong>{m.count}</strong></td>
                      <td><div className="dow-bar-cell"><div className="dow-mini-bar" style={{ width: `${Math.round(pct(m.count, s.total)) * 5}px`, background: PALETTE[i % PALETTE.length] }} /><span style={{ fontSize: 11, color: 'var(--muted)' }}>{pctStr(m.count, s.total)}</span></div></td>
                    </tr>
                  ))}
                />
              </div>
            </div>

            {/* Attempts vs outcome */}
            <div className="dow-grid-2">
              <Card title="🔢 Attempts vs. Outcome — Real Conversion Rate" badge="Booked % and Lost % per bucket">
                <div className="dow-effort">
                  <div className="dow-effort-box low">
                    <div className="val">{pctStr(derived.lowBooked, derived.lowTotal)} booked</div>
                    <div className="lbl">0–3 attempts · {fmtNum(derived.lowTotal)} leads · {pctStr(derived.lowLost, derived.lowTotal)} lost</div>
                  </div>
                  <div className="dow-effort-box high">
                    <div className="val">{pctStr(derived.highBooked, derived.highTotal)} booked</div>
                    <div className="lbl">4+ attempts · {fmtNum(derived.highTotal)} leads · {pctStr(derived.highLost, derived.highTotal)} lost</div>
                  </div>
                </div>
                {s.bucketOutcome.map((b, i) => (
                  <div className="dow-prog" key={b.bucket}>
                    <span className="dow-prog-label">{b.bucket}</span>
                    <div className="dow-prog-track"><div className="dow-prog-fill" style={{ width: `${Math.min(b.bookedRate + b.lostRate, 100)}%`, background: [C.slate, C.red, C.amber, C.blue, C.purple, C.green][i] }} /></div>
                    <span className="dow-prog-val">{fmtNum(b.total)} leads · {b.bookedRate}% booked / {b.lostRate}% lost</span>
                  </div>
                ))}
                <div className="dow-note">
                  {derived.peak
                    ? `Booking rate peaks at ${derived.peak.bucket} (${derived.peak.bookedRate}%), while the lost rate reaches ${derived.topBucket10?.lostRate ?? 0}% for leads worked 10+ times. Past the peak, extra attempts mostly delay marking a lead lost — consider a hard cap with a switch to nurture messaging beyond that point.`
                    : 'No worked leads in this view yet — attempt-wise conversion will appear once activities are logged.'}
                </div>
              </Card>
              <Card title="Booked % vs Lost % by Attempt Bucket" badge="Lead-level join">
                <div className="dow-chart tall"><BucketOutcomeChart buckets={s.bucketOutcome} /></div>
              </Card>
            </div>

            {/* Insights */}
            <div className="dow-grid-2">
              <div>
                <div className="dow-sec-head"><h2>📈 Scope of Improvement</h2></div>
                <div className="dow-card">
                  <Insight icon="⚠️" color="red" title="Low Overall Conversion" chip={pctStr(s.booked, s.total)}
                    desc={`${s.booked} bookings from ${fmtNum(s.total)} leads — ${pctStr(s.booked, s.qualified)} of Qualified leads convert${derived.peak ? `, and the booking rate peaks at ${derived.peak.bucket} (${derived.peak.bookedRate}%)` : ''}. Focus closing effort in that attempt window instead of over-calling past it.`} />
                  {topLost.length > 0 && (
                    <Insight icon="📞" color="amber"
                      title={`"${topLost[0].name}"${topLost[1] ? ` + "${topLost[1].name}"` : ''} Dominate Losses`}
                      chip={pctStr(topLost[0].count + (topLost[1]?.count || 0), derived.lostSum)}
                      desc={`Together these account for ${topLost[0].count + (topLost[1]?.count || 0)} of ${derived.lostSum} lost leads. Add a scripted objection-handling step before a lead can be marked lost for these reasons — the biggest reason is usually the cheapest to trim.`} />
                  )}
                  {topModels[0] && (
                    <Insight icon="🚗" color="green" title="Top Models Anchor the Funnel"
                      chip={pctStr(topModels[0].count + (topModels[1]?.count || 0), derived.modelSum)}
                      desc={`${topModels[0].name} (${topModels[0].count})${topModels[1] ? ` and ${topModels[1].name} (${topModels[1].count})` : ''} make up ${pctStr(topModels[0].count + (topModels[1]?.count || 0), derived.modelSum)} of leads with a model. Keep dedicated product experts and ready comparison sheets for these.`} />
                  )}
                  <Insight icon="📅" color="blue" title="Pipeline Concentration"
                    desc={`${pctStr(s.live, s.total)} of this view's leads are still Live, and ${fmtNum(s.untouched)} (${pctStr(s.untouched, s.total)}) have no activity logged yet. Prioritise first contact on the untouched cohort before it drifts cold — the lost rate climbs sharply in the higher-attempt zone.`} />
                  {derived.topExec && (
                    <Insight icon="🏆" color="purple" title="Owner Load Disparity"
                      desc={`${derived.topExec.name} currently holds ${fmtNum(derived.topExec.count)} leads (${pctStr(derived.topExec.count, s.total)} of total), while ${fmtNum(s.heldAtAdmin)} leads (${pctStr(s.heldAtAdmin, s.total)}) are still parked at admin level. Distributing earlier — even Cold leads — gives every lead a named owner.`} />
                  )}
                </div>
              </div>
              <div>
                <div className="dow-sec-head"><h2>🔍 Lost Reason Patterns</h2></div>
                <div className="dow-card">
                  {topLost.length ? topLost.slice(0, 5).map((r, i) => (
                    <Insight key={r.name} icon={['🔕', '🏪', '🔄', '💬', '📍'][i]} color={['red', 'amber', 'blue', 'purple', 'green'][i]}
                      title={r.name} chip={pctStr(r.count, derived.lostSum)}
                      desc={`${r.count} of ${derived.lostSum} lost leads in this view were closed with this reason.`} />
                  )) : <div className="dow-insight-desc">No lost-reason data in this view.</div>}
                </div>
              </div>
            </div>

            <div className="dow-grid-2">
              <div>
                <div className="dow-sec-head"><h2>🌐 Slow-Converting Buyer Strategy</h2><span className="dow-badge">Data-informed guidance</span></div>
                <div className="dow-card">
                  <Insight icon="🧭" color="blue" title={`Cold buyers are ${pctStr(derived.slow, s.total)} of this view`}
                    desc={`${fmtNum(derived.slow)} leads said 3–6 months or "just enquiring" — customers who are engaging but not ready. Move this segment onto a 30/45/60-day nurture cadence instead of closing them out after a few calls.`} />
                  <Insight icon="🎥" color="green" title="Lead with information on first touch"
                    desc="A first WhatsApp message with a price/variant PDF or a short video — instead of an immediate sales call — matches how a research-stage buyer wants to be engaged." />
                  <Insight icon="⏱️" color="amber" title="Reserve top executives for Hot / Warm, not raw volume"
                    desc={`${fmtNum(derived.hot)} leads are Hot (immediate buyers). Route those to the strongest closers first; ${fmtNum(s.heldAtAdmin)} leads still sit at admin level without a field owner.`} />
                  {derived.topSource && (
                    <Insight icon="🔁" color="purple" title={`${SOURCE_LABEL(derived.topSource.name)} brings ${pctStr(derived.topSource.count, s.total)} of leads`}
                      desc="Measure booking rate per source separately, so spend follows conversions rather than volume — the Funnel and Group reports break this down per executive." />
                  )}
                </div>
              </div>
              <div>
                <div className="dow-sec-head"><h2>🧹 Data Quality Notes</h2></div>
                <div className="dow-card">
                  {(() => {
                    const items = [];
                    if (s.noModel)     items.push(['📦', 'amber',  'Model not captured',        `${fmtNum(s.noModel)} leads (${pctStr(s.noModel, s.total)}) have no Model recorded.`]);
                    if (s.noCategory)  items.push(['🏷️', 'blue',   'Buying time not tagged',    `${fmtNum(s.noCategory)} leads (${pctStr(s.noCategory, s.total)}) have no buying time, which limits Hot/Warm/Cold prioritisation.`]);
                    if (s.untouched)   items.push(['📭', 'red',    'Leads with no activity',    `${fmtNum(s.untouched)} leads (${pctStr(s.untouched, s.total)}) have never had a call or visit logged.`]);
                    if (s.overdue)     items.push(['⏰', 'purple', 'Overdue follow-ups',        `${fmtNum(s.overdue)} live leads have a follow-up date that has already passed.`]);
                    if (s.heldAtAdmin) items.push(['🧑‍💼', 'green',  'Leads without a field owner', `${fmtNum(s.heldAtAdmin)} leads (${pctStr(s.heldAtAdmin, s.total)}) are still assigned to an admin rather than a dealer or executive.`]);
                    if (derived.unspecified) items.push(['❓', 'amber', 'Lost without a reason', `${fmtNum(derived.unspecified)} lost leads have no lost reason recorded.`]);
                    return items.length
                      ? items.map(it => <Insight key={it[2]} icon={it[0]} color={it[1]} title={it[2]} desc={it[3]} />)
                      : <div className="dow-insight-desc">No notable data-quality gaps detected in this view.</div>;
                  })()}
                </div>
              </div>
            </div>

            {/* Booking vs leads + qualification split */}
            <div className="dow-grid-2">
              <Card title="Month-wise Booking vs Leads">
                <div className="dow-chart"><BookingChart months={months} /></div>
              </Card>
              <Card title="Qualified vs Not-Qualified — Outcome Split">
                <div className="dow-chart"><QualSplitChart qual={s.qualOutcome} nqual={s.nqualOutcome} /></div>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
